import React, { useEffect, useState } from 'react';
import { Routes, Route, NavLink, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Preloader from './components/Preloader';
import { PermissionProvider } from './contexts/PermissionContext';
import { Can } from './components/Can';
// Admin Pages - Dashboard
import Dashboard from './pages/Admin/Dashboard/Dashboard';
import DashboardEnhanced from './pages/Admin/Dashboard/DashboardEnhanced';

// Admin Pages - Users
import Users from './pages/Admin/Users/Users';
import Roles from './pages/Admin/Users/Roles';
import Permissions from './pages/Admin/Users/Permissions';

// Admin Pages - Jobs
import Jobs from './pages/Admin/Jobs/Jobs';
import PublicApplications from './pages/Admin/Jobs/PublicApplications';

// Admin Pages - Assessments
import Assessments from './pages/Admin/Assessments/Assessments';
import AssessmentManagement from './pages/Admin/Assessments/AssessmentManagement';
import AssessmentBuilder from './pages/Admin/Assessments/AssessmentBuilder';
import TechnicalAssessments from './pages/Admin/Assessments/TechnicalAssessments';
import TechnicalSubmissions from './pages/Admin/Assessments/TechnicalSubmissions';

// Admin Pages - Candidates
import Candidates from './pages/Admin/Candidates/Candidates';
import CandidateEvaluation from './pages/Admin/Candidates/CandidateEvaluation';
import Evaluation from './pages/Admin/Candidates/Evaluation';
import Results from './pages/Admin/Candidates/Results';

// Admin Pages - System
import Logs from './pages/Admin/System/Logs';

// Candidate Portal Pages
import CandidateLogin from './pages/CandidatePortal/CandidateLogin';
import CandidateSSNCompletion from './pages/CandidatePortal/CandidateSSNCompletion';
import CandidateDashboard from './pages/CandidatePortal/CandidateDashboardNew';
import CandidateDashboardAssessments from './pages/CandidatePortal/CandidateDashboardAssessments';
import CandidateApplications from './pages/CandidatePortal/CandidateApplications';
import CandidateTechnicalTask from './pages/CandidatePortal/CandidateTechnicalTask';
import TechnicalTaskSubmission from './pages/CandidatePortal/TechnicalTaskSubmission';
import DigitalAssessment from './pages/CandidatePortal/DigitalAssessment';
import DigitalAssessmentNew from './pages/CandidatePortal/DigitalAssessmentNew';

// Public Pages
import LandingPage from './pages/Public/LandingPage';
import PublicJobApplication from './pages/Public/PublicJobApplication';
import AdminLogin from './pages/Public/AdminLogin';
import Login from './pages/Public/Login';
import NotFound from './pages/Public/NotFound';
import ProtectedRoute from "./components/ProtectedRoute";
import { showInfo } from "./utils/sweetAlertConfig";


const navGroups = [
  {
    title: 'Overview',
    icon: 'fa-home',
    items: [
      { to: '/dashboard', icon: 'fa-gauge', label: 'Dashboard', permission: 'dashboard.view' },
    ]
  },
  {
    title: 'HR Management',
    icon: 'fa-users',
    items: [
      { to: '/candidates', icon: 'fa-user-tie', label: 'Candidates', permission: 'candidates.view' },
      { to: '/jobs', icon: 'fa-briefcase', label: 'Jobs', permission: 'jobs.view' },
      { to: '/applications', icon: 'fa-file-alt', label: 'Job Applications', permission: 'applications.view' },
    ]
  },
  {
    title: 'Assessment',
    icon: 'fa-clipboard-check',
    items: [
      { to: '/assessment-management', icon: 'fa-tasks', label: 'Assessment Management', permission: 'assessments.manage' },
      { to: '/assessments', icon: 'fa-clipboard-list', label: 'Digital Assessments', permission: 'assessments.view' },
      { to: '/technical-assessments', icon: 'fa-code', label: 'Technical Tasks', permission: 'assessments.view' },
      { to: '/technical-submissions', icon: 'fa-file-text', label: 'Review Submissions', permission: 'assessments.evaluate' },
      { to: '/evaluation', icon: 'fa-star-half-stroke', label: 'Evaluation', permission: 'applications.evaluate' },
      { to: '/candidate-evaluation', icon: 'fa-user-check', label: 'Candidate Rating', permission: 'candidates.evaluate' },
      { to: '/results', icon: 'fa-chart-bar', label: 'Results', permission: 'results.view' },
    ]
  },
  {
    title: 'System',
    icon: 'fa-cogs',
    items: [
      { to: '/users', icon: 'fa-users-cog', label: 'Users', permission: 'users.view' },
      { to: '/roles', icon: 'fa-shield-halved', label: 'Roles', permission: 'roles.view' },
      { to: '/permissions', icon: 'fa-key', label: 'Permissions', permission: 'permissions.view' },
      { to: '/logs', icon: 'fa-clipboard-list', label: 'Logs & Notes', permission: 'logs.view' },
    ]
  }
];

function Sidebar({ 
  expandedGroups, 
  toggleGroup, 
  isMobileMenuOpen, 
  toggleMobileMenu, 
  isSidebarCollapsed 
}) {

  return (
    <nav className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <img 
          src={isSidebarCollapsed ? "/icon.png" : "/ZonNet.png"} 
          alt="ZonNet Logo" 
          className="logo" 
        />
        <button 
          className="mobile-menu-toggle d-md-none"
          onClick={toggleMobileMenu}
        >
          <i className={`fa-solid ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
        </button>
      </div>
      
      <div className="nav-groups">
        {navGroups.map((group) => (
          <div key={group.title} className="nav-group">
            <div 
              className="nav-group-header"
              onClick={() => toggleGroup(group.title)}
            >
              <i className={`fa-solid ${group.icon}`}></i>
              <span className="group-title">{group.title}</span>
              <i className={`fa-solid fa-chevron-${expandedGroups[group.title] ? 'down' : 'right'} group-toggle`}></i>
            </div>
            
            {expandedGroups[group.title] && (
              <ul className="nav-group-items">
                {group.items.map((item) => (
                  <Can key={item.to} permission={item.permission}>
                    <li>
                      <NavLink to={item.to} className="nav-link">
                        <i className={`fa-solid ${item.icon}`}></i>
                        <span>{item.label}</span>
                      </NavLink>
                    </li>
                  </Can>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      
      <div className="sidebar-footer">
        <NavLink to="/logout" className="nav-link logout-link">
          <i className="fa-solid fa-right-from-bracket"></i>
          <span>Logout</span>
        </NavLink>
      </div>
    </nav>
  );
}

function Header({ onToggleMobileMenu, onToggleSidebar }) {
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = React.useState(false);
  const [user, setUser] = React.useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    // Get user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error('Failed to parse user data', e);
      }
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleProfileSettings = () => {
    setIsProfileDropdownOpen(false);
    // TODO: Navigate to profile settings page when implemented
    showInfo(
      'Profile Settings',
      'This feature will be implemented soon! You will be able to manage your profile information here.'
    );
  };

  const handleAccountSettings = () => {
    setIsProfileDropdownOpen(false);
    // TODO: Navigate to account settings page when implemented
    showInfo(
      'Account Settings',
      'This feature will be implemented soon! You will be able to manage your account preferences here.'
    );
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-info')) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="header">
      <div className="header-left">
        <button
          className="sidebar-toggle-btn d-none d-md-block"
          onClick={onToggleSidebar}
        >
          <i className="fa-solid fa-bars"></i>
        </button>
        <button
          className="mobile-menu-btn d-md-none"
          onClick={onToggleMobileMenu}
        >
          <i className="fa-solid fa-bars"></i>
        </button>
        <div className="header-logo-section">
          <span className="header-title">ZonNet Recruitment System</span>
        </div>
      </div>
      
      <div className="user-info" onClick={toggleProfileDropdown}>
        <div className="user-profile">
          <span className="user-avatar">
            <i className="fa-solid fa-user"></i>
          </span>
          <div className="user-details">
            <span className="user-greeting">Hi, {user?.name || 'User'}</span>
            <span className="user-role">{user?.roles?.[0] || 'Member'}</span>
          </div>
          <i className={`fa-solid fa-chevron-${isProfileDropdownOpen ? 'up' : 'down'} dropdown-arrow`}></i>
        </div>
        
        {isProfileDropdownOpen && (
          <div className="profile-dropdown">
            <div className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleProfileSettings(); }}>
              <i className="fa-solid fa-user me-2"></i>
              <span>Profile Settings</span>
            </div>
            <div className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleAccountSettings(); }}>
              <i className="fa-solid fa-cog me-2"></i>
              <span>Account Settings</span>
            </div>
            <div className="dropdown-divider"></div>
            <div className="dropdown-item logout-item" onClick={(e) => { e.stopPropagation(); handleLogout(); }}>
              <i className="fa-solid fa-right-from-bracket me-2"></i>
              <span>Logout</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Footer() {
  return (
    <div className="footer">&copy; 2025 ZonNet Digital Solutions. All rights reserved.</div>
  );
}

function App() {
  const location = useLocation();

  // Check if user is authenticated
  const isAuthenticated = !!localStorage.getItem('token');

  // Public pages that don't require authentication
  const publicPages = ['/', '/sys-admin-portal', '/apply'];
  const isPublicPage = publicPages.includes(location.pathname) || location.pathname.startsWith('/candidate/');

  // Protected dashboard pages
  const protectedPages = ['/dashboard', '/users', '/jobs', '/applications', '/assessment-management', '/assessments', '/technical-assessments', '/technical-submissions', '/candidates', '/roles', '/permissions', '/evaluation', '/candidate-evaluation', '/results', '/logs', '/logout'];
  const isProtectedPage = protectedPages.includes(location.pathname) || location.pathname.startsWith('/assessment-builder/');

  const isLoginPage = location.pathname === "/sys-admin-portal" || location.pathname === "/" || location.pathname.startsWith("/candidate/") || location.pathname === "/apply";
  const isAssessmentBuilderPage = location.pathname.includes('/assessment-builder/') && new URLSearchParams(location.search).get('standalone') === 'true';

  // If user is NOT authenticated and trying to access protected page OR unknown page, show 404
  const isNotFoundPage = (!isAuthenticated && !isPublicPage) || (!isPublicPage && !isProtectedPage);

  const [loading, setLoading] = useState(false);

  // Sidebar state management
  const [expandedGroups, setExpandedGroups] = React.useState({
    'Overview': true,
    'HR Management': true,
    'Assessment': false,
    'System': false
  });

  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);

  const toggleGroup = (groupTitle) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupTitle]: !prev[groupTitle]
    }));
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 600); // 600ms preloading
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // For Assessment Builder - fullscreen mode without layout (standalone popup)
  if (isAssessmentBuilderPage) {
    return (
      <div className="assessment-builder-wrapper">
        {loading && <Preloader />}
        <Routes>
          <Route path="/assessment-builder/:exam_id" element={<ProtectedRoute><AssessmentBuilder /></ProtectedRoute>} />
        </Routes>
      </div>
    );
  }

  // If it's a 404 page, render only NotFound without any layout
  if (isNotFoundPage) {
    return <NotFound />;
  }

  return (
    <PermissionProvider>
      <div className="dashboard-wrapper">
        {loading && <Preloader />}
      {/* لا تعرض السايدبار والهيدر في صفحة تسجيل الدخول */}
      {!isLoginPage && <Sidebar
        expandedGroups={expandedGroups}
        toggleGroup={toggleGroup}
        isMobileMenuOpen={isMobileMenuOpen}
        toggleMobileMenu={toggleMobileMenu}
        isSidebarCollapsed={isSidebarCollapsed}
      />}
      <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: '100vh' }}>
        {!isLoginPage && <Header onToggleMobileMenu={toggleMobileMenu} onToggleSidebar={toggleSidebar} />}
        <Routes>
          <Route path="/" element={<PublicJobApplication />} />
          <Route path="/sys-admin-portal" element={<AdminLogin />} />
          <Route path="/apply" element={<PublicJobApplication />} />
          <Route path="/candidate/login" element={<CandidateLogin />} />
          <Route path="/candidate/complete-ssn" element={<CandidateSSNCompletion />} />
          <Route path="/candidate/assessments" element={<CandidateDashboardAssessments />} />
          <Route path="/candidate/dashboard" element={<CandidateDashboardAssessments />} />
          <Route path="/candidate/profile" element={<CandidateDashboard />} />
          <Route path="/candidate/dashboard-old" element={<CandidateDashboard />} />
          <Route path="/candidate/applications" element={<CandidateApplications />} />
          <Route path="/candidate/assessment/:assessmentId" element={<DigitalAssessment />} />
          <Route path="/candidate/digital-assessment/:attempt_id" element={<DigitalAssessmentNew />} />
          <Route path="/candidate/technical-task/:assignment_id" element={<TechnicalTaskSubmission />} />
          <Route path="/candidate/technical-assessment/:taskId" element={<CandidateTechnicalTask />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardEnhanced /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
          <Route path="/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
          <Route path="/applications" element={<ProtectedRoute><PublicApplications /></ProtectedRoute>} />
          <Route path="/assessment-management" element={<ProtectedRoute><AssessmentManagement /></ProtectedRoute>} />
          <Route path="/assessments" element={<ProtectedRoute><Assessments /></ProtectedRoute>} />
          <Route path="/assessment-builder/:exam_id" element={<ProtectedRoute><AssessmentBuilder /></ProtectedRoute>} />
          <Route path="/technical-assessments" element={<ProtectedRoute><TechnicalAssessments /></ProtectedRoute>} />
          <Route path="/technical-submissions" element={<ProtectedRoute><TechnicalSubmissions /></ProtectedRoute>} />

          <Route path="/candidates" element={<ProtectedRoute><Candidates /></ProtectedRoute>} />
          <Route path="/roles" element={<ProtectedRoute><Roles /></ProtectedRoute>} />
          <Route path="/permissions" element={<ProtectedRoute><Permissions /></ProtectedRoute>} />
          <Route path="/evaluation" element={<ProtectedRoute><Evaluation /></ProtectedRoute>} />
          <Route path="/candidate-evaluation" element={<ProtectedRoute><CandidateEvaluation /></ProtectedRoute>} />
          <Route path="/results" element={<ProtectedRoute><Results /></ProtectedRoute>} />
          <Route path="/logs" element={<ProtectedRoute><Logs /></ProtectedRoute>} />

          <Route path="/logout" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        {!isLoginPage && <Footer />}
      </div>
      </div>
    </PermissionProvider>
  );
}

export default App; 